import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  extractPrimaryCompanyName,
  extractPrimaryPlaceName,
  formatMoney,
  getReceiptUrls,
  getTollAmount,
  getTotalAmount,
  getTravelAmount,
  padThaiTextForPdf,
} from "./TravelExpenseApprovalUtils";
import signatures from "../../utils/signatures";

Font.register({
  family: "NotoSansThaiTravel",
  fonts: [
    { src: "/fonts/NotoSansThai-Regular.ttf" },
    { src: "/fonts/NotoSansThai-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 26,
    paddingBottom: 20,
    fontSize: 9,
    fontFamily: "NotoSansThaiTravel",
    color: "#111827",
    lineHeight: 1.35,
  },
  pageMuted: {
    color: "#6b7280",
  },
  brandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  brandBlock: {
    maxWidth: "50%",
  },
  brandSubtitle: {
    marginTop: 2,
    fontSize: 9,
    color: "#4b5563",
  },
  docMeta: {
    alignItems: "flex-end",
    gap: 4,
  },
  docMetaBox: {
    borderWidth: 1,
    borderColor: "#374151",
    paddingVertical: 6,
    paddingHorizontal: 10,
    minWidth: 110,
    alignItems: "center",
  },
  docMetaLabel: {
    fontSize: 8,
    color: "#4b5563",
  },
  docMetaValue: {
    fontSize: 10,
    fontWeight: "bold",
  },
  titleBar: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#374151",
    paddingVertical: 6,
    alignItems: "center",
    marginBottom: 8,
  },
  titleText: {
    fontSize: 12,
    fontWeight: "bold",
  },
  infoTable: {
    borderWidth: 1,
    borderColor: "#374151",
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#9ca3af",
  },
  infoCellLabel: {
    width: 92,
    paddingVertical: 5,
    paddingHorizontal: 6,
    backgroundColor: "#f3f4f6",
    borderRightWidth: 1,
    borderRightColor: "#9ca3af",
    fontWeight: "bold",
  },
  infoCellValue: {
    flex: 1,
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  gridTable: {
    borderWidth: 1,
    borderColor: "#111827",
    marginTop: 4,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottomWidth: 1,
    borderBottomColor: "#111827",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    minHeight: 26,
  },
  colDate: { width: "13%" },
  colVehicle: { width: "9%" },
  colOrigin: { width: "16%" },
  colDestination: { width: "16%" },
  colCompany: { width: "14%" },
  colAmount: { width: "10%" },
  colPurpose: { width: "22%" },
  tableCell: {
    paddingVertical: 5,
    paddingHorizontal: 5,
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
    justifyContent: "center",
  },
  tableCellHeader: {
    paddingVertical: 6,
    paddingHorizontal: 5,
    borderRightWidth: 1,
    borderRightColor: "#9ca3af",
    justifyContent: "center",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 8,
  },
  textCenter: { textAlign: "center" },
  textRight: { textAlign: "right" },
  summaryWrap: {
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: "#111827",
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 4,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  remarksBox: {
    borderWidth: 1,
    borderColor: "#111827",
    marginTop: 8,
    padding: 8,
    minHeight: 52,
  },
  remarksLabel: {
    fontWeight: "bold",
    marginBottom: 4,
  },
  signatureTable: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#111827",
    flexDirection: "row",
  },
  signatureCell: {
    flex: 1,
    minHeight: 82,
    borderRightWidth: 1,
    borderRightColor: "#111827",
    justifyContent: "space-between",
    paddingTop: 8,
    paddingHorizontal: 6,
    paddingBottom: 6,
  },
  signatureContent: {
    minHeight: 34,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  signatureName: {
    fontSize: 9,
    textAlign: "center",
    fontWeight: "bold",
  },
  signatureImage: {
    width: 88,
    height: 38,
    objectFit: "contain",
  },
  signatureDate: {
    fontSize: 8,
    color: "#4b5563",
  },
  signatureRole: {
    borderTopWidth: 1,
    borderTopColor: "#9ca3af",
    paddingTop: 5,
    textAlign: "center",
    fontWeight: "bold",
  },
  receiptPage: {
    paddingTop: 24,
    paddingHorizontal: 26,
    paddingBottom: 20,
    fontSize: 9,
    fontFamily: "NotoSansThaiTravel",
    color: "#111827",
    lineHeight: 1.35,
  },
  receiptPageTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  receiptPageSubtitle: {
    fontSize: 9,
    color: "#4b5563",
    marginBottom: 10,
  },
  receiptSheet: {
    flex: 1,
    marginTop: 6,
  },
  receiptCard: {
    width: "100%",
    minHeight: 640,
    justifyContent: "center",
    alignItems: "center",
  },
  receiptImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  receiptCaption: {
    marginTop: 8,
    fontSize: 8,
    color: "#374151",
    textAlign: "center",
  },
  receiptPlaceholder: {
    fontSize: 10,
    color: "#6b7280",
    textAlign: "center",
  },
  pageNumber: {
    position: "absolute",
    bottom: 10,
    right: 24,
    fontSize: 8,
    color: "#6b7280",
  },
});

const MONTHS_SHORT = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

const toDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatThaiDate = (value, includeTime = false) => {
  const date = toDate(value);
  if (!date) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear() + 543).slice(-2);

  if (!includeTime) {
    return `${day}/${month}/${year}`;
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatThaiLongDate = (value) => {
  const date = toDate(value);
  if (!date) return "-";

  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${
    date.getFullYear() + 543
  }`;
};

const extractRequesterName = (item) => {
  const preferredName = String(
    item?.requestedByFullName ||
      item?.requestedByUser?.firstName ||
      item?.requestedBy ||
      ""
  ).trim();
  const text = preferredName;
  if (!text) return "-";

  const explicitLastName = String(item?.requestedByUser?.lastName || "").trim();
  if (explicitLastName && !text.includes(explicitLastName)) {
    return `${text} ${explicitLastName}`.trim();
  }

  if (text.includes("@")) {
    return text.split("@")[0].trim() || "-";
  }

  return text.split(",").map((part) => part.trim()).find(Boolean) || "-";
};

const extractLocationName = (value) => {
  return extractPrimaryPlaceName(value);
};

const extractContactCompany = (item) => {
  const preferredName = String(
    item?.contactCompanyName ||
      item?.clientName ||
      item?.projectName ||
      item?.quotationTitle ||
      item?.destination ||
      ""
  ).trim();
  const text = preferredName;
  if (!text) return "-";
  return extractPrimaryCompanyName(text);
};

const buildExportRows = (item) => [
  {
    date: formatThaiDate(item?.departureDateTime),
    vehicle: "Car",
    origin: padThaiTextForPdf(extractLocationName(item?.origin)),
    destination: padThaiTextForPdf(extractLocationName(item?.destination)),
    company: padThaiTextForPdf(extractContactCompany(item)),
    amount: formatMoney(getTotalAmount(item)),
    purpose: padThaiTextForPdf(String(item?.note || "-").trim() || "-"),
  },
];

const resolveReceiptImageSrc = (receipt) =>
  receipt?.dataUrl || receipt?.signedUrl || receipt?.originalUrl || "";

const buildReceiptImageSource = (receipt) => {
  const uri = resolveReceiptImageSrc(receipt);
  if (!uri) return null;

  return { uri };
};

const APPROVAL_SIGNERS = {
  OPTX: {
    email: "chawachai@optx.co.th",
    name: "CHAWACHAI WICHAIDIT",
  },
  NEON: {
    email: "natorn@neonworks.co.th",
    name: "NATORN MANEEKORN",
  },
};

const resolveApprovalSigner = (companyName) => {
  const normalizedCompany = String(companyName || "").trim().toUpperCase();
  return normalizedCompany.includes("NEON")
    ? APPROVAL_SIGNERS.NEON
    : APPROVAL_SIGNERS.OPTX;
};

const buildSignatureDisplayName = (value) => String(value || "").trim();

const renderSignatureContent = ({ name, signatureUrl }) => {
  const displayName = buildSignatureDisplayName(name);

  if (signatureUrl) {
    return (
      <View style={styles.signatureContent}>
        <Image style={styles.signatureImage} src={signatureUrl} />
        {displayName ? (
          <Text style={styles.signatureName}>{displayName}</Text>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.signatureContent}>
      {displayName ? <Text style={styles.signatureName}>{displayName}</Text> : null}
    </View>
  );
};

const ReceiptCard = ({ receipt, fallbackLabel }) => {
  if (!receipt) {
    return (
      <View style={[styles.receiptSheet, styles.receiptCard]}>
        <Text style={styles.receiptPlaceholder}>ไม่มีหลักฐานแนบในรายการนี้</Text>
      </View>
    );
  }

  if (receipt.type === "image" && resolveReceiptImageSrc(receipt)) {
    return (
      <View style={styles.receiptSheet}>
        <View style={styles.receiptCard}>
          <Image
            source={buildReceiptImageSource(receipt)}
            style={styles.receiptImage}
          />
        </View>
        <Text style={styles.receiptCaption}>{receipt.label || fallbackLabel}</Text>
      </View>
    );
  }

  return (
    <View style={[styles.receiptSheet, styles.receiptCard]}>
      <Text style={styles.receiptPlaceholder}>
        {receipt.label || fallbackLabel}
      </Text>
      <Text style={[styles.receiptCaption, styles.pageMuted]}>
        ไฟล์ประเภท PDF แนบอยู่ในระบบ
      </Text>
    </View>
  );
};

const TravelExpenseExportDocument = ({ records, generatedAt, companyName }) => {
  let pageNumber = 1;
  const approvalSigner = resolveApprovalSigner(companyName);

  return (
    <Document>
      {records.flatMap((record) => {
        const receiptFiles = Array.isArray(record?.signedReceiptFiles)
          ? record.signedReceiptFiles
          : getReceiptUrls(record).map((url, index) => ({
              originalUrl: url,
              signedUrl: url,
              type: "image",
              label: `หลักฐาน ${index + 1}`,
            }));

        const requesterSignatureName = buildSignatureDisplayName(
          record?.requestedByFullName
        );
        const reviewerSignatureName = buildSignatureDisplayName(
          record?.lv3ApproverFullName
        );
        const reviewerSignatureDate = record?.lv3ApprovedAt
          ? `วันที่ ${formatThaiDate(record?.lv3ApprovedAt)}`
          : "วันที่ __________________";
        const approverSignatureUrl = record?.isFullyApproved
          ? signatures[approvalSigner.email]
          : "";
        const approverSignatureName = record?.isFullyApproved
          ? approvalSigner.name
          : "";
        const approverSignatureDate = record?.isFullyApproved && record?.finalApprovedAt
          ? `วันที่ ${formatThaiDate(record?.finalApprovedAt)}`
          : "วันที่ __________________";
        const rows = buildExportRows(record);
        const receiptCount = Math.max(receiptFiles.length, 1);
        const totalPagesForRecord = receiptCount + 1;
        const formPageNo = pageNumber;
        pageNumber += totalPagesForRecord;

        const pages = [
          <Page key={`${record._id}-form`} size="A4" style={styles.page}>
            <View style={styles.brandRow}>
              <View style={styles.brandBlock}>
                <Text style={styles.brandSubtitle}>
                  แบบเบิกค่าพาหนะ ค่าน้ำมัน / ค่าทางด่วน /
                </Text>
              </View>
              <View style={styles.docMeta}>
                <View style={styles.docMetaBox}>
                  <Text style={styles.docMetaLabel}>วันที่จัดทำเอกสาร</Text>
                  <Text style={styles.docMetaValue}>
                    {formatThaiLongDate(generatedAt)}
                  </Text>
                  <Text style={[styles.docMetaLabel, { marginTop: 4 }]}>
                    เลขที่เอกสาร
                  </Text>
                  <Text style={styles.docMetaValue}>
                    {record?.documentNo || "-"}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.titleBar}>
              <Text style={styles.titleText}>
                ใบเบิกค่าพาหนะ / ค่าน้ำมัน / ค่าทางด่วน /
              </Text>
            </View>

            <View style={styles.infoTable}>
              <View style={[styles.infoRow, { borderBottomWidth: 0 }]}>
                <Text style={styles.infoCellLabel}>ผู้ขอเบิก</Text>
                <Text style={styles.infoCellValue}>
                  {extractRequesterName(record)}
                </Text>
              </View>
            </View>
            <View style={styles.gridTable}>
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.tableCellHeader, styles.colDate]}>
                  วัน/เดือน/ปี เดินทาง
                </Text>
                <Text style={[styles.tableCellHeader, styles.colVehicle]}>
                  พาหนะ
                </Text>
                <Text style={[styles.tableCellHeader, styles.colOrigin]}>
                  {"\u0E15\u0E49\u0E19\u0E17\u0E32\u0E07"}
                </Text>
                <Text style={[styles.tableCellHeader, styles.colDestination]}>
                  {"\u0E1B\u0E25\u0E32\u0E22\u0E17\u0E32\u0E07"}
                </Text>
                <Text style={[styles.tableCellHeader, styles.colCompany]}>
                  บริษัทที่ติดต่อ
                </Text>
                <Text style={[styles.tableCellHeader, styles.colAmount]}>
                  จำนวนเงิน (บาท)
                </Text>
                <Text
                  style={[
                    styles.tableCellHeader,
                    styles.colPurpose,
                    { borderRightWidth: 0 },
                  ]}
                >
                  จุดประสงค์
                </Text>
              </View>

              {rows.map((row, index) => (
                <View
                  key={`${record._id}-row-${index}`}
                  style={[
                    styles.tableRow,
                    index === rows.length - 1 && { borderBottomWidth: 0 },
                  ]}
                >
                  <Text
                    style={[styles.tableCell, styles.colDate, styles.textCenter]}
                  >
                    {row.date}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.colVehicle,
                      styles.textCenter,
                    ]}
                  >
                    {row.vehicle}
                  </Text>
                  <Text style={[styles.tableCell, styles.colOrigin]}>
                    {row.origin}
                  </Text>
                  <Text style={[styles.tableCell, styles.colDestination]}>
                    {row.destination}
                  </Text>
                  <Text style={[styles.tableCell, styles.colCompany]}>
                    {row.company}
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.colAmount, styles.textRight]}
                  >
                    {row.amount}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.colPurpose,
                      { borderRightWidth: 0 },
                    ]}
                  >
                    {row.purpose}
                  </Text>
                </View>
              ))}
            </View>

            <View style={styles.summaryWrap}>
              <View style={styles.summaryRow}>
                <Text>ค่าเดินทาง</Text>
                <Text>{formatMoney(getTravelAmount(record))} บาท</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text>ค่าทางด่วน</Text>
                <Text>{formatMoney(getTollAmount(record))} บาท</Text>
              </View>
              <View style={[styles.summaryRow, { marginTop: 2 }]}>
                <Text style={{ fontWeight: "bold" }}>รวมเป็นเงิน</Text>
                <Text style={{ fontWeight: "bold" }}>
                  {formatMoney(getTotalAmount(record))} บาท
                </Text>
              </View>
            </View>

            <View style={styles.remarksBox}>
              <Text style={styles.remarksLabel}>หมายเหตุ</Text>
              <Text>
                {record?.note
                  ? record.note
                  : "เอกสารฉบับนี้ถูกสร้างจากระบบ Travel Expense เพื่อใช้ประกอบการเบิกค่าเดินทาง"}
              </Text>
            </View>

            <View style={styles.signatureTable}>
              <View style={styles.signatureCell}>
                <Text style={styles.signatureDate}>
                  วันที่ {formatThaiDate(record?.departureDateTime)}
                </Text>
                {renderSignatureContent({ name: requesterSignatureName })}
                <Text style={styles.signatureRole}>ผู้เบิก</Text>
              </View>
              <View style={styles.signatureCell}>
                <Text style={styles.signatureDate}>{reviewerSignatureDate}</Text>
                {renderSignatureContent({ name: reviewerSignatureName })}
                <Text style={styles.signatureRole}>ผู้ตรวจสอบ / MANAGER</Text>
              </View>
              <View style={[styles.signatureCell, { borderRightWidth: 0 }]}>
                <Text style={styles.signatureDate}>{approverSignatureDate}</Text>
                {renderSignatureContent({
                  name: approverSignatureName,
                  signatureUrl: approverSignatureUrl,
                })}
                <Text style={styles.signatureRole}>ผู้อนุมัติ</Text>
              </View>
            </View>

            <Text style={styles.pageNumber}>หน้า {formPageNo}</Text>
          </Page>,
        ];

        if (receiptFiles.length === 0) {
          pages.push(
            <Page
              key={`${record._id}-receipts-empty`}
              size="A4"
              style={styles.receiptPage}
            >
              <Text style={styles.receiptPageTitle}>หลักฐานแนบประกอบการเบิก</Text>
              <Text style={styles.receiptPageSubtitle}>
                {"\u0E1C\u0E39\u0E49\u0E02\u0E2D: "}{extractRequesterName(record)}
              </Text>

              <ReceiptCard fallbackLabel="หลักฐาน" />
              <Text style={styles.pageNumber}>หน้า {formPageNo + 1}</Text>
            </Page>
          );

          return pages;
        }

        receiptFiles.forEach((receipt, receiptIndex) => {
          pages.push(
            <Page
              key={`${record._id}-receipt-${receiptIndex}`}
              size="A4"
              style={styles.receiptPage}
            >
              <Text style={styles.receiptPageTitle}>หลักฐานแนบประกอบการเบิก</Text>
              <Text style={styles.receiptPageSubtitle}>
                {"\u0E1C\u0E39\u0E49\u0E02\u0E2D: "}{extractRequesterName(record)}
              </Text>

              <ReceiptCard
                receipt={receipt}
                fallbackLabel={`หลักฐาน ${receiptIndex + 1}`}
              />

              <Text style={styles.pageNumber}>
                หน้า {formPageNo + receiptIndex + 1}
              </Text>
            </Page>
          );
        });

        return pages;
      })}
    </Document>
  );
};

export default TravelExpenseExportDocument;
